import React, { useState } from 'react'
import styles from './SelfVerification.module.scss'
import InformationDisplay from '../../../../components/InformationDisplay/InformationDisplay'
import { useCallback, useEffect } from 'react'
import { certificateNice } from '../../../../utils/certificateNice'
import { useLocation } from 'react-router-dom'
import axios from 'axios'
import { useIsMobile } from '../../../../hooks'

const SelfVerification = ({
	verifiedStatus,
	changeVerifiedStatus,
	onVerificationSuccess, // 콜백함수 -> form 의 데이터 채우기
	onCertData = null,
	noMarginTop = false,
	noGap = false,
}) => {
	const [message, setMessage] = useState('')
	const verifiedStatusClassname = styles[verifiedStatus]
	const [info, setInfo] = useState({ name: '', phoneNumber: '' })
	const location = useLocation()
	const isMobile = useIsMobile()

	const NICE_CERT_STORAGE_KEY = 'niceCertificationResult'
	const NICE_CERT_START_KEY = 'niceCertificationStart'

	// 테스트용: true면 Nice 본인인증 없이 즉시 성공 처리
	const SKIP_NICE_VERIFICATION_FOR_TEST = false

	// 인증 결과 처리 함수
	const processCertificationResult = useCallback(
		(result) => {
			const { status, message: resultMessage, userInfo } = result

			if (status === 'success') {
				console.log('Nice 본인인증 최종 성공! 받은 데이터:', userInfo)
				const newInfo = {
					name: userInfo.name,
					phoneNumber: userInfo.phoneNumber, // Ensure this matches your backend's field name (phone vs phoneNumber)
				}
				setInfo(newInfo) // Update local state for display
				changeVerifiedStatus('success') // Update status in parent for UI logic
				setMessage('본인인증에 성공했습니다.') // Display success message
				onVerificationSuccess(newInfo) // Callback to parent to fill form fields
			} else {
				console.error('Nice 본인인증 최종 실패:', resultMessage)
				setInfo({ name: '', phoneNumber: '' }) // Clear info on failure
				changeVerifiedStatus('failed') // Update status in parent
				setMessage(`본인인증에 실패했어요: ${resultMessage}`) // Display failure message
			}
		},
		[changeVerifiedStatus, onVerificationSuccess],
	)

	// Handler for messages received from the popup window
	const handleMessageFromPopup = useCallback(
		(event) => {
			// IMPORTANT: Validate the origin for security!
			// This should match the exact domain of your parent page (where SelfVerification is rendered).
			// For local development, it might be 'http://localhost:3000'.
			// For production, it must be 'https://your-actual-domain.com'.
			// The popup (CertifyComplete.jsx) sends with window.location.origin.
			if (event.origin !== window.location.origin) {
				console.warn('Message received from unknown origin:', event.origin)
				return // Ignore messages from untrusted origins
			}

			// Check if it's the specific message type from your Nice certification complete page
			if (event.data && event.data.type === 'niceCertificationFinalResult') {
				processCertificationResult(event.data)
			}
		},
		[processCertificationResult],
	) // Dependencies for useCallback

	// sessionStorage 및 localStorage에서 인증 결과 확인 (새 창으로 이동한 경우)
	const checkStorageForResult = useCallback(() => {
		try {
			// 인증 시작 정보 확인
			const certStart = localStorage.getItem(NICE_CERT_START_KEY)
			if (!certStart) {
				// 인증 시작 정보가 없으면 확인하지 않음
				return
			}

			const startInfo = JSON.parse(certStart)
			const elapsed = Date.now() - startInfo.timestamp

			// 30초 이내이고 실패 메시지가 있으면 무시 (아직 진행 중일 수 있음)
			if (elapsed < 30 * 1000) {
				// sessionStorage 먼저 확인
				let storedResult = sessionStorage.getItem(NICE_CERT_STORAGE_KEY)
				if (!storedResult) {
					// sessionStorage에 없으면 localStorage 확인
					storedResult = localStorage.getItem(NICE_CERT_STORAGE_KEY)
				}

				if (storedResult) {
					const result = JSON.parse(storedResult)
					// 30초 이내의 실패 메시지는 무시 (아직 진행 중)
					if (result.status === 'failed' && elapsed < 30 * 1000) {
						console.log(
							'30초 이내 실패 메시지 무시 (아직 진행 중일 수 있음)',
							elapsed,
							'ms',
						)
						return
					}
					console.log('storage에서 인증 결과 발견:', result)
					processCertificationResult(result)
					// 처리 후 storage에서 제거
					sessionStorage.removeItem(NICE_CERT_STORAGE_KEY)
					localStorage.removeItem(NICE_CERT_STORAGE_KEY)
					localStorage.removeItem(NICE_CERT_START_KEY) // 인증 시작 정보도 제거
				}
			} else {
				// 30초 이상 경과한 경우에만 실패 메시지 처리
				let storedResult = sessionStorage.getItem(NICE_CERT_STORAGE_KEY)
				if (!storedResult) {
					storedResult = localStorage.getItem(NICE_CERT_STORAGE_KEY)
				}

				if (storedResult) {
					const result = JSON.parse(storedResult)
					console.log('storage에서 인증 결과 발견:', result)
					processCertificationResult(result)
					// 처리 후 storage에서 제거
					sessionStorage.removeItem(NICE_CERT_STORAGE_KEY)
					localStorage.removeItem(NICE_CERT_STORAGE_KEY)
					localStorage.removeItem(NICE_CERT_START_KEY)
				}
			}
		} catch (error) {
			console.error('storage에서 인증 결과 읽기 실패:', error)
		}
	}, [processCertificationResult])

	// Effect hook to add and remove the message event listener
	useEffect(() => {
		window.addEventListener('message', handleMessageFromPopup)

		// Cleanup function: remove the event listener when the component unmounts
		return () => {
			window.removeEventListener('message', handleMessageFromPopup)
		}
	}, [handleMessageFromPopup]) // Re-run effect if handleMessageFromPopup changes

	// URL 파라미터에서 인증 결과 처리 (모바일: 백엔드에서 리다이렉트된 결과만 처리)
	const processUrlParams = useCallback(async () => {
		const urlParams = new URLSearchParams(location.search)

		// 백엔드에서 리다이렉트된 결과 확인 (모바일)
		// 웹 환경은 CertifyComplete.js에서 처리하므로 여기서는 모바일 결과만 처리
		if (urlParams.get('cert') === 'complete') {
			const status = urlParams.get('status')
			console.log('백엔드에서 리다이렉트된 인증 결과 발견:', { status })

			if (status === 'success') {
				const name = urlParams.get('name')
				const phone = urlParams.get('phone')

				if (name && phone) {
					const newInfo = {
						name: decodeURIComponent(name),
						phoneNumber: phone,
					}

					// 결과 처리
					processCertificationResult({
						status: 'success',
						message: '본인인증이 성공적으로 완료되었습니다.',
						userInfo: newInfo,
					})

					// URL에서 파라미터 제거 (깔끔한 URL 유지)
					window.history.replaceState({}, '', location.pathname)

					// 인증 시작 정보 제거
					localStorage.removeItem(NICE_CERT_START_KEY)
					return
				}
			} else if (status === 'failed') {
				const message = urlParams.get('message')
				processCertificationResult({
					status: 'failed',
					message: message ? decodeURIComponent(message) : '본인인증에 실패했습니다.',
					userInfo: null,
				})

				// URL에서 파라미터 제거
				window.history.replaceState({}, '', location.pathname)
				localStorage.removeItem(NICE_CERT_START_KEY)
				return
			}
		}
		// 웹 환경: NICE URL 파라미터 처리는 CertifyComplete.js에서 수행하므로 여기서는 처리하지 않음
	}, [location.search, location.pathname, processCertificationResult])

	// 컴포넌트 마운트 시 및 페이지 포커스 시 확인
	useEffect(() => {
		// URL 파라미터 먼저 확인 (Pass 앱에서 직접 돌아온 경우)
		processUrlParams()

		// 모바일 환경: URL 파라미터만 확인 (storage 체크 불필요)
		if (isMobile) {
			// 페이지 포커스 시 URL 파라미터만 확인
			const handleFocus = () => {
				console.log('모바일: 페이지 포커스 - URL 파라미터 확인')
				processUrlParams()
			}

			// visibilitychange 이벤트로도 확인
			const handleVisibilityChange = () => {
				if (!document.hidden) {
					console.log('모바일: 페이지가 다시 보임 - URL 파라미터 확인')
					processUrlParams()
				}
			}

			window.addEventListener('focus', handleFocus)
			document.addEventListener('visibilitychange', handleVisibilityChange)

			return () => {
				window.removeEventListener('focus', handleFocus)
				document.removeEventListener('visibilitychange', handleVisibilityChange)
			}
		} else {
			// 웹 환경: 팝업 메시지와 storage 모두 확인
			// 초기 마운트 시 storage 확인
			checkStorageForResult()

			// 페이지 포커스 시 확인 (새 창에서 돌아왔을 때)
			const handleFocus = () => {
				console.log('웹: 페이지 포커스 - storage 확인')
				processUrlParams()
				checkStorageForResult()
			}

			// visibilitychange 이벤트로도 확인 (탭 전환 시)
			const handleVisibilityChange = () => {
				if (!document.hidden) {
					console.log('웹: 페이지가 다시 보임 - storage 확인')
					processUrlParams()
					checkStorageForResult()
				}
			}

			// 주기적으로 확인 (웹에서 새 창으로 이동한 경우)
			const checkInterval = setInterval(() => {
				const certStart = localStorage.getItem(NICE_CERT_START_KEY)
				if (certStart) {
					const startTime = JSON.parse(certStart).timestamp
					const now = Date.now()
					// 5분 이내의 인증 시도만 확인 (타임아웃 방지)
					if (now - startTime < 5 * 60 * 1000) {
						checkStorageForResult()
					} else {
						// 타임아웃된 경우 정리
						localStorage.removeItem(NICE_CERT_START_KEY)
					}
				}
			}, 2000) // 2초마다 확인

			window.addEventListener('focus', handleFocus)
			document.addEventListener('visibilitychange', handleVisibilityChange)

			return () => {
				window.removeEventListener('focus', handleFocus)
				document.removeEventListener('visibilitychange', handleVisibilityChange)
				clearInterval(checkInterval)
			}
		}
	}, [checkStorageForResult, processUrlParams, isMobile])

	const handleVerificationNice = async () => {
		if (SKIP_NICE_VERIFICATION_FOR_TEST) {
			const newInfo = { name: '테스트이름', phoneNumber: '010-1234-5678' }
			changeVerifiedStatus('success')
			setInfo(newInfo)
			setMessage('본인인증에 성공했습니다. (테스트 모드)')
			onVerificationSuccess(newInfo)
			return
		}

		try {
			const currentPath = window.location.pathname
			const currentUrl = window.location.origin + currentPath // 전체 URL (origin + pathname)

			// 환경에 따라 returnURL 설정
			// - 웹: 클라이언트 /certify/complete (GET 파라미터로 처리)
			// - 모바일: 백엔드 서버 /certify/complete (POST로 처리 후 리다이렉트)
			let returnURL
			if (isMobile) {
				// 모바일: 백엔드 서버로 설정 (redirect 쿼리 파라미터에 전체 URL 포함)
				const apiEndpoint = process.env.REACT_APP_API_ENDPOINT || window.location.origin
				returnURL = `${apiEndpoint}/users/certify/complete?redirect=${encodeURIComponent(currentUrl)}`
			} else {
				// 웹: 클라이언트로 설정
				returnURL = `${window.location.origin}/certify/complete`
			}

			console.log('시도중', { currentPath, currentUrl, returnURL, isMobile })

			// 인증 시작 정보를 localStorage에 저장 (모바일 앱 전환 대비)
			localStorage.setItem(
				NICE_CERT_START_KEY,
				JSON.stringify({
					timestamp: Date.now(),
					returnURL: returnURL,
					originalPath: currentPath, // 원래 페이지 경로 저장 (certify/complete에서 사용)
				}),
			)

			await certificateNice(returnURL)
			// 팝업 여부는 certify/complete에서 window.opener로 확인하여 처리

			// encData와 tokenVersionId는 나중에 사용할 수 있도록 저장 (필요한 경우)
			// const { encData, tokenVersionId } = await certificationNice(returnURL)
			// onCertData && onCertData({ encData, tokenVersionId })
			// const result = await randomCheckVerification()

			// if (result.success) {
			// 	const newInfo = { name: result.name, phoneNumber: result.phoneNumber }
			// 	setInfo(newInfo)
			// 	changeVerifiedStatus('success')
			// 	setMessage('본인인증에 성공했습니다')
			// 	onVerificationSuccess(newInfo)
			// 	console.log('성공')
			// } else {
			// 	setInfo({ classNameame: '', phoneNumber: '' })
			// 	changeVerifiedStatus('failed')
			// 	setMessage('본인인증에 실패했어요')
			// }
		} catch (error) {
			// setInfo({ name: '', phoneNumber: '' })
			// changeVerifiedStatus('failed')
			// setMessage('인증 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.')
			// 로컬 템프
			// const newInfo = { name: '홍캐디', phoneNumber: '010-1234-5678' }
			// changeVerifiedStatus('success')
			// setInfo(newInfo)
			// setMessage('본인인증에 성공했습니다.')
			// onVerificationSuccess(newInfo) // 부모 컴포넌트의 폼에 데이터 전달
			console.error('인증 오류:', error)
		}
	}

	// const handleVerificationTemp = async () => {
	// 	try {
	// 		const returnURL = 'http://localhost:3000/certify/complete'
	// 		console.log('시도중')
	// 		// const result = await certification(returnURL) // 나중에 백엔드 연동할 부분
	// 		const result = await randomCheckVerification()

	// 		if (result.success) {
	// 			const newInfo = { name: result.name, phoneNumber: result.phoneNumber }
	// 			setInfo(newInfo)
	// 			changeVerifiedStatus('success')
	// 			setMessage('본인인증에 성공했습니다')
	// 			onVerificationSuccess(newInfo)
	// 			console.log('성공')
	// 		} else {
	// 			setInfo({ classNameame: '', phoneNumber: '' })
	// 			changeVerifiedStatus('failed')
	// 			setMessage('본인인증에 실패했어요')
	// 		}
	// 	} catch (error) {
	// 		setInfo({ name: '', phoneNumber: '' })
	// 		changeVerifiedStatus('failed')
	// 		setMessage('인증 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.')
	// 		console.error('인증 오류:', error)
	// 	}
	// }

	const infoList = [
		{ label: '이름', value: info.name },
		{ label: '휴대폰번호', value: info.phoneNumber },
	]

	return (
		<>
			<div className={`${styles.selfAuthBox} ${noMarginTop ? styles.noMarginTop : ''}`}>
				본인인증
				<button className={styles.selfAuth} type="button" onClick={handleVerificationNice}>
					인증하기
				</button>
			</div>
			{/* <button
				className={styles.selfAuth}
				type="button"
				onClick={() => handleVerificationTemp()}>
				넘어가기
			</button> */}
			<div className={`${verifiedStatusClassname}`}>{message}</div>
			<div
				className={`${styles.flexCol} ${verifiedStatus === 'success' ? '' : styles.none} ${
					noGap ? styles.noGap : ''
				}`}>
				<InformationDisplay infoList={infoList}></InformationDisplay>
			</div>
		</>
	)
}

export default SelfVerification
